/**
 * useAskUploads — the Ask composer's file-attachment lane (D32-lite, upload spec §5.5 SPA half).
 *
 * The server side already ships: POST/GET /api/surfaces/uploads distills a dropped file into a
 * chat-scoped note the answer path (searchAttachments, U6) can read. This hook is the client half —
 * the paperclip/drag-drop entry point, the per-file status chips, and the honest limits.
 *
 * CHAT BINDING. An upload belongs to a chat. But at bare /ask the chat is not minted until the first
 * answer persists — so a file dropped into a brand-new thread would have nothing to attach to. The
 * route solves this with mint-on-attach (`newChat`): when there is no chatId, the POST mints one and
 * returns it, and we adopt it as the active thread (`adoptChat`) so the first question already carries
 * it and the URL becomes /ask/<chatId>. Everything here keys on that chat id.
 *
 * HONEST LIMITS (bug-664). The route's EFFECTIVE per-file cap is ~4 MiB (the platform request-body
 * ceiling), not the 64 MiB policy number. We pre-check against the effective cap for instant feedback
 * and let the server's typed failure_reason be authoritative — a rejected file lands as a `rejected`
 * chip carrying its reason, never a silent drop.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUpload, apiFetch, ApiError } from '../lib/api';

// A file's lifecycle in the chip. Mirrors user_uploads.status (migration 169) plus the client-only
// `uploading` state, which is the window between the user's drop and the server's first row.
export type UploadStatus = 'uploading' | 'queued' | 'extracting' | 'distilling' | 'ready' | 'failed' | 'rejected';

export interface UploadItem {
  // The server row id once it exists; a client temp id (`tmp-…`) while the POST is in flight, so a chip
  // can render the instant the user drops, before the server has minted anything.
  uploadId: string;
  filename: string;
  // The card's meta line (icon tile + "PDF · 1.2 MB"). From the server row once it exists (the GET
  // already serves both); seeded from the local File during the client-only 'uploading' window so the
  // optimistic card carries real meta from the first frame. Null on an older row — the card degrades
  // to its extension label alone.
  mimeType: string | null;
  byteSize: number | null;
  status: UploadStatus;
  failureReason: string | null;
  // The turn this file was SENT with (mig 171), or null while it is still composer-side. Server-derived;
  // the client-side twin during the claim's round trip is pendingClaimRef.
  turnId: string | null;
  // Transport progress 0..1 while status is the client-only 'uploading' (XHR upload.onprogress). Null when
  // the browser reports none — the chip's ring falls back to indeterminate. Meaningless after the POST.
  progress: number | null;
}

// The route's `limits` payload (a subset — what the UI shows/enforces). Server is authoritative; these
// mirror the route so we can pre-check before the first POST tells us the real numbers.
export interface UploadLimits {
  maxFiles: number;
  maxBytes: number; // the EFFECTIVE per-file cap (~4 MiB), what we actually enforce
  policyMaxFileBytes: number; // the 64 MiB policy number, shown as the stated ceiling
  minBytes: number;
  extensions: string[];
}

// Mirror of lib/uploads/limits.ts, used ONLY for instant client pre-flight before the server's `limits`
// payload arrives. The server re-checks and is authoritative — these can never be laxer than the route.
const CLIENT_LIMITS: UploadLimits = {
  maxFiles: 5,
  maxBytes: 4 * 1024 * 1024 - 8 * 1024, // EFFECTIVE_MAX_FILE_BYTES: min(64MiB, 4MiB - framing)
  policyMaxFileBytes: 64 * 1024 * 1024,
  minBytes: 1024,
  extensions: ['.pdf', '.docx', '.xlsx', '.pptx', '.csv', '.tsv', '.md', '.txt', '.json', '.xml', '.html'],
};

const UPLOADS_PATH = '/api/surfaces/uploads';
const POLL_MS = 2500;
const TERMINAL: ReadonlySet<UploadStatus> = new Set(['ready', 'failed', 'rejected']);

// The client-side pre-flight (spec §7): instant feedback before the POST, against the SAME rules the
// route enforces. Pure and exported so it is unit-testable without a render harness — the honest-limits
// enforcement is exactly the thing a "looks fine" implementation gets subtly wrong. Returns a friendly
// message to show, or null when every file passes. The server RE-CHECKS and is authoritative; this can
// never be laxer than the route (a file this lets through may still land as a `rejected` chip).
export function preflightAttach(files: File[], alreadyAttached: number, limits: UploadLimits): string | null {
  if (files.length === 0) return null;
  if (alreadyAttached + files.length > limits.maxFiles) return `Up to ${limits.maxFiles} files per chat.`;
  const tooBig = files.find((f) => f.size > limits.maxBytes);
  if (tooBig) return `"${tooBig.name}" is too large (max ~${Math.floor(limits.maxBytes / (1024 * 1024))} MB per file).`;
  const tooSmall = files.find((f) => f.size < limits.minBytes);
  if (tooSmall) return `"${tooSmall.name}" is too small (min 1 KB).`;
  const badType = files.find((f) => !limits.extensions.some((ext) => f.name.toLowerCase().endsWith(ext)));
  if (badType) return `"${badType.name}" is not a supported type.`;
  return null;
}

// Human-readable, honest failure text. The user must learn which rule they hit (spec §7); a typed
// reason from the server maps here, and an unknown reason degrades to a plain sentence, never a code.
export function uploadFailureText(reason: string | null): string {
  switch (reason) {
    case 'too_large':
    case 'request_too_large':
      return 'Too large (max ~4 MB per file)';
    case 'too_small':
      return 'Too small (min 1 KB)';
    case 'unsupported_type':
      return 'Unsupported file type';
    case 'content_mismatch':
    case 'unsupported_encoding':
      return "File contents didn't match its type";
    case 'daily_file_limit':
    case 'daily_byte_limit':
      return 'Daily upload limit reached';
    case 'no_text_layer':
      return 'No readable text (a scan?)';
    case 'extraction_failed':
    case 'extracted_too_large':
      return "Couldn't read this file";
    case 'distill_failed':
      return "Couldn't index this file";
    case 'capture_paused':
      return 'Uploads are paused';
    default:
      return reason ? 'Upload failed' : 'Failed';
  }
}

// The send gate's predicate (bug-721): TRUE while any attachment is non-terminal
// (uploading/queued/extracting/distilling). Pure and exported for the unit test — the composer blocks
// send on exactly this, so its edges (failed/rejected never block; empty never blocks) must be pinned.
export function uploadsBlockSend(uploads: readonly Pick<UploadItem, 'status'>[]): boolean {
  return uploads.some((u) => !TERMINAL.has(u.status));
}

// The send-time split (message placement, Teddy 2026-08-12), pure and exported for the unit test:
// ready composer chips CLAIM onto the sent message; failed/rejected chips CLEAR (a message never
// carries a file the answer cannot use — the failure was already shown in the composer); working
// chips cannot reach here (uploadsBlockSend refuses the send) but are passed through untouched as
// the safe default if they somehow do.
export function partitionForSend(
  uploads: readonly (Pick<UploadItem, 'uploadId' | 'filename' | 'status'> &
    Partial<Pick<UploadItem, 'mimeType' | 'byteSize'>>)[],
): {
  claim: AskAttachment[];
  clear: string[];
} {
  const claim: AskAttachment[] = [];
  const clear: string[] = [];
  for (const u of uploads) {
    if (u.status === 'ready')
      claim.push({
        uploadId: u.uploadId,
        filename: u.filename,
        mimeType: u.mimeType ?? null,
        byteSize: u.byteSize ?? null,
        status: u.status,
      });
    else if (u.status === 'failed' || u.status === 'rejected') clear.push(u.uploadId);
  }
  return { claim, clear };
}

// What a sent message carries per file — the minimal render surface for an on-message attachment
// card (and the persist body's uploadIds source). Status rides along so a resumed thread can show
// a failed-after-send file honestly.
export interface AskAttachment {
  uploadId: string;
  filename: string;
  // Meta for the on-message card's tile + "type · size" line; null degrades to the extension label.
  mimeType: string | null;
  byteSize: number | null;
  status: UploadStatus;
}

export interface UseAskUploads {
  uploads: UploadItem[];
  // The COMPOSER's chips: unclaimed rows only — a file already sent with a message (turnId set, or
  // claim-pending after claimForSend) leaves the chat bar. This is what the chip row and the send
  // gate read; `uploads` above stays the full chat list for the message-placement join.
  composerUploads: UploadItem[];
  // Server-claimed rows grouped by their turn id — AskSurface joins these onto persisted turns so a
  // reloaded thread shows each file on the message it was sent with (mig 171).
  attachmentsByTurn: Record<string, AskAttachment[]>;
  // THE SEND HOOK (message placement, Teddy 2026-08-12): called by submit() at the moment a question
  // fires. Ready composer chips move onto the message (returned for the optimistic bubble + the
  // persist body's uploadIds; held out of the composer via the pending-claim set until the server
  // confirms); failed/rejected chips clear (the message never carries a file the answer cannot use).
  // Working chips cannot exist here — the send gate refuses to fire while any are.
  claimForSend: () => AskAttachment[];
  limits: UploadLimits;
  attaching: boolean;
  // A client-side pre-flight refusal (too many, too big, wrong type) shown inline; cleared on the next
  // attach. The SERVER refusals arrive as `rejected` chips instead — this is only the instant local one.
  attachError: string | null;
  attachFiles: (files: File[]) => void;
  dismiss: (uploadId: string) => void;
  // TRUE when at least one upload is still working (uploading/queued/extracting/distilling). Drives the
  // composer's SEND GATE (bug-721, Teddy 2026-08-12: a question must not fire before its file has
  // landed — the engine only registers searchAttachments for READY uploads) and the status line that
  // says so. Supersedes U9 §5.5's disclose-don't-block posture at the composer; the honest-notice
  // frames stay as belt-and-suspenders for resumed threads and races.
  indexing: boolean;
}

/**
 * @param activeChatId the current Ask thread, or null for a not-yet-minted one.
 * @param adoptChat     called with the id the route mints on a bare-thread attach, so the thread and its
 *                      URL follow the file. A no-op when the attach already had a chatId.
 */
export function useAskUploads(activeChatId: string | null, adoptChat: (id: string) => void): UseAskUploads {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [limits, setLimits] = useState<UploadLimits>(CLIENT_LIMITS);
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  // The chat id the `uploads` state currently reflects. It follows activeChatId, EXCEPT across a
  // mint-on-attach: attachFiles sets it to the freshly minted id BEFORE adoptChat propagates activeChatId
  // back, so the thread-switch effect below does not wipe the chips we just showed for that same chat.
  const reflectedChatIdRef = useRef<string | null>(activeChatId);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A monotonic thread-switch counter, bumped whenever the active thread changes (below). attachFiles
  // captures it at the drop; if it has moved by the time the POST resolves, the user switched threads
  // mid-upload — so the resolved chips are NOT written into the now-current thread and the mint is NOT
  // adopted (adoptChat also refuses to clobber a real thread). The bare→bare "New chat" case (null→null)
  // does not fire the switch effect and is benign: the fresh bare thread simply becomes the file's chat.
  const threadGenRef = useRef(0);
  // Single-flight for the bare-thread mint: while the first no-chat attach is minting, this holds a
  // promise of its chat id so concurrent attaches bind to the SAME chat instead of each minting one.
  const mintPromiseRef = useRef<Promise<string | null> | null>(null);
  // Chips the user dismissed (bug-721 escape hatch: a WORKING chip is dismissable so a stranded server
  // row can never brick the send gate). The poll re-lists the server's rows wholesale, so without this
  // set a dismissed chip would resurrect on the next tick. Client-view-only, cleared on thread switch —
  // the server row is untouched (per-file deletion is U7's purge arm, not this affordance).
  const dismissedRef = useRef<Set<string>>(new Set());
  // Files claimForSend moved onto a message whose server claim (the transcript POST's uploadIds) has
  // not landed yet. The poll re-lists rows wholesale and a claim-pending row still reads turnId null
  // server-side — without this set it would bounce back into the composer for the seconds between
  // send and persist. An id leaves the set the moment its server row arrives claimed; cleared on
  // thread switch like dismissals. If the persist ultimately fails (best-effort), the ids stay
  // pending for the session and the row honestly returns to the composer on the next thread load.
  const pendingClaimRef = useRef<Set<string>>(new Set());
  // Server uploadIds attached DURING THIS THREAD-SESSION (the attach POST's returned rows). This is
  // what claimForSend is allowed to claim: pre-migration-171 the server never stamps turn_id, so on
  // a reload every historical row looks unclaimed — without this gate the next question would weld
  // every previously-sent file onto its message (adversarial review 2026-08-12). Rows NOT attached
  // this session stay honest composer chips (pre-171's status quo) instead of being claimed.
  const sessionAttachedRef = useRef<Set<string>>(new Set());
  const withoutDismissed = useCallback(
    (items: UploadItem[]) =>
      items
        .filter((u) => !dismissedRef.current.has(u.uploadId))
        .map((u) => {
          if (u.turnId) pendingClaimRef.current.delete(u.uploadId); // server confirmed — the set has done its job
          return u;
        }),
    [],
  );

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Poll a chat's upload rows until every one is terminal. Keyed on a chat id captured at scheduling time,
  // so a poll that resolves after the user switched threads is discarded rather than written into the new
  // thread's chips (the same generation-guard shape useAskQuery uses for persists).
  const pollOnce = useCallback(
    (chatId: string) => {
      stopPolling();
      pollTimerRef.current = setTimeout(async () => {
        if (reflectedChatIdRef.current !== chatId) return; // switched away — abandon this loop
        try {
          const { uploads: rows } = await apiFetch<{ uploads: ServerUpload[] }>(
            `${UPLOADS_PATH}?chatId=${encodeURIComponent(chatId)}`,
          );
          if (reflectedChatIdRef.current !== chatId) return;
          const items = withoutDismissed((rows ?? []).map(fromServer));
          setUploads(items);
          if (items.some((u) => !TERMINAL.has(u.status))) pollOnce(chatId);
        } catch {
          // A transient poll failure is not terminal — try again on the next tick rather than freezing a
          // chip at "extracting" forever. If the chat changed, the guard above stops the loop.
          if (reflectedChatIdRef.current === chatId) pollOnce(chatId);
        }
      }, POLL_MS);
    },
    [stopPolling],
  );

  // Thread switched (newChat/loadChat/delete-active) → reset to that chat's stored uploads. A brand-new
  // thread (null) clears the chips. The reflectedChatIdRef guard skips the reset when activeChatId is
  // just catching up to a chat we already minted-and-attached into (no flicker, no double load).
  useEffect(() => {
    if (reflectedChatIdRef.current === activeChatId) return;
    reflectedChatIdRef.current = activeChatId;
    threadGenRef.current += 1; // a real thread switch — invalidate any in-flight attach's adoption/chips
    mintPromiseRef.current = null; // a stale bare-thread mint must not bind a new thread's attaches
    dismissedRef.current = new Set(); // dismissals are per-thread view state
    pendingClaimRef.current = new Set(); // claim-pending is too — a switched-in thread re-reads the server truth
    sessionAttachedRef.current = new Set(); // ...and what was "attached this session" is per-thread by definition
    stopPolling();
    setUploads([]);
    setAttachError(null);
    setAttaching(false); // a switch frees the paperclip immediately, without waiting on the old POST
    if (!activeChatId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { uploads: rows } = await apiFetch<{ uploads: ServerUpload[] }>(
          `${UPLOADS_PATH}?chatId=${encodeURIComponent(activeChatId)}`,
        );
        if (cancelled || reflectedChatIdRef.current !== activeChatId) return;
        const items = withoutDismissed((rows ?? []).map(fromServer));
        setUploads(items);
        if (items.some((u) => !TERMINAL.has(u.status))) pollOnce(activeChatId);
      } catch {
        // Best-effort: a failed list load just leaves the chip row empty for a resumed thread.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeChatId, pollOnce, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const attachFiles = useCallback(
    (files: File[]) => {
      setAttachError(null);
      if (files.length === 0) return;

      // CLIENT PRE-FLIGHT (instant feedback; the server re-checks and is authoritative). A rejected/failed
      // chip does not count toward the ceiling — only live attachments do. Counted over the COMPOSER
      // set, not the whole chat: a file already sent with an earlier message left the chip row, and
      // counting it would dead-end the paperclip against chips the user can no longer see (the server
      // has no per-chat cap — its 5 is per request; adversarial review 2026-08-12).
      const already = uploads.filter(
        (u) =>
          u.status !== 'rejected' &&
          u.status !== 'failed' &&
          u.turnId === null &&
          !pendingClaimRef.current.has(u.uploadId),
      ).length;
      const preflightError = preflightAttach(files, already, limits);
      if (preflightError) {
        setAttachError(preflightError);
        return;
      }

      // Optimistic chips so the user sees their files the instant they drop, before the POST resolves.
      const temps: UploadItem[] = files.map((f, i) => ({
        uploadId: `tmp-${Date.now()}-${i}`,
        filename: f.name,
        mimeType: f.type || null,
        byteSize: f.size,
        status: 'uploading',
        failureReason: null,
        turnId: null,
        progress: null,
      }));
      setUploads((prev) => [...prev, ...temps]);
      setAttaching(true);

      const chatIdAtAttach = activeChatId;
      const genAtAttach = threadGenRef.current;

      // SINGLE-FLIGHT THE BARE-THREAD MINT. Two drops inside the first POST's latency window both read
      // activeChatId === null and would each send newChat=1 — the server mints TWO chats, the composer
      // ends up showing one file while the question posts the OTHER chat's id, and the shown file is
      // exactly the one the answer cannot read. So only the FIRST concurrent bare-thread attach mints;
      // the rest wait for its chat (decided synchronously here, so a second attachFiles in the same tick
      // sees the in-flight mint) and bind to the SAME id.
      let waitForMint: Promise<string | null> | null = null;
      let ownsMint = false;
      let resolveMint: ((id: string | null) => void) | undefined;
      if (chatIdAtAttach === null) {
        if (mintPromiseRef.current) {
          waitForMint = mintPromiseRef.current;
        } else {
          ownsMint = true;
          mintPromiseRef.current = new Promise<string | null>((r) => {
            resolveMint = r;
          });
        }
      }

      void (async () => {
        try {
          // A non-owner bare-thread attach waits for the owner's minted chat, then binds to it instead of
          // minting again. Null back means the owner's mint FAILED — fall back to minting ourselves.
          let boundChatId = chatIdAtAttach;
          if (waitForMint) {
            boundChatId = await waitForMint;
            if (threadGenRef.current !== genAtAttach) return; // switched threads while waiting
          }

          const form = new FormData();
          for (const f of files) form.append('files', f);
          if (boundChatId) form.append('chatId', boundChatId);
          else form.append('newChat', '1'); // the mint owner (or a waiter whose owner-mint failed)

          // One POST carries the whole batch, so its transport fraction drives every chip in it —
          // the standard determinate loading circle, filled as the bytes actually move (Teddy
          // 2026-08-12). After a thread switch the temps are gone and the map matches nothing.
          const tempIds = new Set(temps.map((t) => t.uploadId));
          const res = await apiUpload<UploadPostResponse>(UPLOADS_PATH, form, (fraction) => {
            setUploads((prev) =>
              prev.map((u) => (tempIds.has(u.uploadId) && u.status === 'uploading' ? { ...u, progress: fraction } : u)),
            );
          });

          // Hand the minted id to any waiters BEFORE the thread-switch guard below can `return` — a waiter
          // parked on this promise must be released even if we ourselves have since switched away.
          if (ownsMint) {
            resolveMint?.(res.chatId ?? null);
            mintPromiseRef.current = null;
          }

          // The user switched to a REAL thread while this upload was in flight → its chips and its mint
          // belong to a thread that is no longer showing. Do not write them into the now-current thread
          // and do not adopt the mint (adoptChat would refuse the clobber anyway). The upload still landed
          // server-side, bound to the chat it was minted for; it re-appears if the user returns to it.
          if (threadGenRef.current !== genAtAttach) return;

          if (res.limits) setLimits((prev) => ({ ...prev, ...res.limits }));

          // The response rows are index-aligned with the request's files (the route processes them in
          // order), which is what lets a mid-transport dismissal STICK: the user ✕'d a `tmp-` chip whose
          // server row didn't exist yet — carry that dismissal onto the real uploadId or the re-list
          // below would resurrect the chip under an id dismissedRef has never seen.
          if (res.uploads.length === temps.length) {
            res.uploads.forEach((r, i) => {
              if (dismissedRef.current.has(temps[i].uploadId)) dismissedRef.current.add(r.uploadId);
            });
          }
          // Everything this POST landed was attached THIS session — the set claimForSend may claim from.
          for (const r of res.uploads) {
            if (!dismissedRef.current.has(r.uploadId)) sessionAttachedRef.current.add(r.uploadId);
          }

          const mintedChatId = res.chatId ?? boundChatId;
          // Adopt a minted chat BEFORE writing chips, and point reflectedChatIdRef at it, so the
          // thread-switch effect treats the incoming activeChatId change as already-reflected. Compared
          // against `boundChatId` (the chat this attach was actually bound to — a waiter's bound id is the
          // owner's mint, so it correctly does NOT re-adopt).
          if (mintedChatId && mintedChatId !== boundChatId) {
            reflectedChatIdRef.current = mintedChatId;
            adoptChat(mintedChatId);
          }

          // Replace the temp chips with the server's authoritative rows for this chat (a re-list is the
          // simplest correct reconciliation — dedupe, duplicates, and rejections all resolve server-side).
          if (mintedChatId) {
            try {
              const { uploads: rows } = await apiFetch<{ uploads: ServerUpload[] }>(
                `${UPLOADS_PATH}?chatId=${encodeURIComponent(mintedChatId)}`,
              );
              if (reflectedChatIdRef.current === mintedChatId) {
                const items = withoutDismissed((rows ?? []).map(fromServer));
                setUploads(items);
                if (items.some((u) => !TERMINAL.has(u.status))) pollOnce(mintedChatId);
                // A re-drop of a file already SENT with an earlier message dedupes server-side to the
                // claimed row — which renders on that message, not in the composer, so without a word
                // here the drop reads as silently swallowed. Say what happened; not an error state.
                const resent = res.uploads.find((r) => {
                  const row = items.find((u) => u.uploadId === r.uploadId);
                  return r.duplicate === true && row?.turnId != null;
                });
                if (resent) setAttachError(`"${resent.filename}" is already attached to an earlier message in this thread.`);
              }
            } catch {
              // The POST succeeded; a failed re-list just leaves the optimistic chips until the next poll.
              if (reflectedChatIdRef.current === mintedChatId) pollOnce(mintedChatId);
            }
          }
        } catch (e) {
          // The mint owner failed → release waiters with null so they fall back to minting themselves
          // (an already-failing path; a fresh mint is the right recovery). Clear the ref either way.
          if (ownsMint) {
            resolveMint?.(null);
            mintPromiseRef.current = null;
          }
          // The POST itself failed (network, or a request-level refusal like too_many_files). Mark the
          // temp chips failed with the server's typed reason where we have it — never a silent removal.
          // Same switch guard: a failure that resolves after a thread switch must not stamp a rejected
          // chip onto the thread the user moved to.
          if (threadGenRef.current !== genAtAttach) return;
          const reason = e instanceof ApiError ? ((e.body as { error?: string })?.error ?? null) : null;
          setUploads((prev) =>
            prev.map((u) =>
              temps.some((t) => t.uploadId === u.uploadId) ? { ...u, status: 'rejected' as const, failureReason: reason } : u,
            ),
          );
        } finally {
          // Always clear — `attaching` is a global "an upload POST is in flight" flag (it disables the
          // paperclip), not thread-scoped state, so clearing it after a switch is correct.
          setAttaching(false);
        }
      })();
    },
    [activeChatId, adoptChat, limits, pollOnce, uploads],
  );

  const dismiss = useCallback((uploadId: string) => {
    // Client-only: drops a chip from view — a rejected/failed one the user has read, or (bug-721) a
    // WORKING one the user unattaches to lift the send gate. It does not delete the server row —
    // per-file server deletion is U7's purge arm, not this composer affordance. Remembered in
    // dismissedRef so the poll's wholesale re-list cannot resurrect it.
    dismissedRef.current.add(uploadId);
    setUploads((prev) => prev.filter((u) => u.uploadId !== uploadId));
  }, []);

  const claimForSend = useCallback((): AskAttachment[] => {
    const composer = uploads.filter((u) => u.turnId === null && !pendingClaimRef.current.has(u.uploadId));
    const { claim: claimable, clear } = partitionForSend(composer);
    // Only rows attached THIS thread-session are claimable (see sessionAttachedRef) — historical
    // unclaimed rows (a pre-migration-171 thread on reload) stay composer chips rather than welding
    // onto the new message. The CLEAR arm is not session-gated: a failed/rejected chip (temp ids
    // included) leaves the composer on send regardless of when it appeared.
    const claim = claimable.filter((c) => sessionAttachedRef.current.has(c.uploadId));
    // A temp chip (`tmp-…`) can't be ready — ready only ever arrives on a server row — so every
    // claimed id is a real uuid the transcript POST can name.
    for (const c of claim) pendingClaimRef.current.add(c.uploadId);
    for (const id of clear) dismissedRef.current.add(id);
    if (clear.length > 0) setUploads((prev) => prev.filter((u) => !dismissedRef.current.has(u.uploadId)));
    return claim;
  }, [uploads]);

  // The composer's view: unclaimed rows only. Claimed (or claim-pending) files live on their message.
  const composerUploads = uploads.filter((u) => u.turnId === null && !pendingClaimRef.current.has(u.uploadId));

  // Server-claimed rows, grouped for the message-placement join. The GET lists newest-first, so the
  // reversed walk puts each turn's files back in the order they were attached.
  const attachmentsByTurn: Record<string, AskAttachment[]> = {};
  for (const u of [...uploads].reverse()) {
    if (!u.turnId) continue;
    (attachmentsByTurn[u.turnId] ??= []).push({
      uploadId: u.uploadId,
      filename: u.filename,
      mimeType: u.mimeType,
      byteSize: u.byteSize,
      status: u.status,
    });
  }

  // The send gate reads the COMPOSER set: a file already sent with an earlier message is by
  // construction terminal-ready and must not re-block later questions.
  const indexing = uploadsBlockSend(composerUploads);

  return { uploads, composerUploads, attachmentsByTurn, claimForSend, limits, attaching, attachError, attachFiles, dismiss, indexing };
}

// ── server shapes (GET/POST /api/surfaces/uploads) ─────────────────────────────────────────────
interface ServerUpload {
  uploadId: string;
  filename: string;
  status: UploadStatus;
  failureReason: string | null;
  // The GET has served both since U4b; optional + `?? null` so an older shape degrades, never breaks.
  mimeType?: string | null;
  byteSize?: number | null;
  // Absent from an older server / pre-migration-171 — `.?? null` everywhere it is read.
  turnId?: string | null;
}
interface UploadPostResponse {
  uploads: (ServerUpload & { duplicate?: boolean })[];
  chatId: string | null;
  limits?: UploadLimits;
}
function fromServer(r: ServerUpload): UploadItem {
  return {
    uploadId: r.uploadId,
    filename: r.filename,
    mimeType: r.mimeType ?? null,
    byteSize: typeof r.byteSize === 'number' ? r.byteSize : null,
    status: r.status,
    failureReason: r.failureReason ?? null,
    turnId: r.turnId ?? null,
    progress: null,
  };
}
